import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DownloadResume = () => {
  const [downloadCounts, setDownloadCounts] = useState({
    pdf: 1247,
    word: 892
  });

  const resumeData = {
    lastUpdated: "September 15, 2024",
    fileSize: {
      pdf: "2.4 MB",
      word: "1.8 MB"
    },
    languages: ["English"],
    versions: [
      {
        type: "pdf",
        name: "Bello_Fawas_Resume.pdf",
        icon: "FileText",
        description: "Optimized for ATS systems and online viewing",
        features: [
          "ATS-friendly format",
          "Hyperlinked portfolio projects",
          "Professional typography",
          "Print-ready quality"
        ]
      },
      {
        type: "word",
        name: "Bello_Fawas_Resume.docx",
        icon: "FileEdit",
        description: "Editable format for customization",
        features: [
          "Fully editable content",
          "Custom formatting options",
          "Easy to modify for specific roles",
          "Compatible with all versions"
        ]
      }
    ]
  };

  const handleDownload = (type) => {
    // Simulate download
    setDownloadCounts(prev => ({
      ...prev,
      [type]: prev?.[type] + 1
    }));

    // In a real application, this would trigger the actual download
    const link = document.createElement('a');
    link.href = `/assets/resume/Bello_Fawas_Resume.${type}`;
    link.download = resumeData?.versions?.find(v => v?.type === type)?.name;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const quickStats = [
    {
      label: "Years Experience",
      value: "8+",
      icon: "Calendar"
    },
    {
      label: "Projects Completed",
      value: "50+",
      icon: "Briefcase"
    },
    {
      label: "Technologies",
      value: "25+",
      icon: "Code"
    },
    {
      label: "Team Size Led",
      value: "12",
      icon: "Users"
    }
  ];

  return (
    <section className="bg-surface py-16">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Download Resume
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Get a comprehensive overview of my experience, skills, and achievements
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {quickStats?.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Icon name={stat?.icon} size={24} className="text-primary" />
              </div>
              <div className="text-2xl font-bold text-foreground mb-1">
                {stat?.value}
              </div>
              <div className="text-sm text-muted-foreground">
                {stat?.label}
              </div>
            </div>
          ))}
        </div>

        {/* Resume Download Options */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {resumeData?.versions?.map((version, index) => (
            <div key={index} className="bg-background rounded-xl p-6 border border-border shadow-subtle">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={version?.icon} size={24} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    {version?.type?.toUpperCase()} Format
                  </h3>
                  <p className="text-muted-foreground text-sm mb-2">
                    {version?.description}
                  </p>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>Size: {resumeData?.fileSize?.[version?.type]}</span>
                    <span>•</span>
                    <span>{downloadCounts?.[version?.type]} downloads</span>
                  </div>
                </div>
              </div>

              <ul className="space-y-2 mb-6">
                {version?.features?.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="Check" size={16} className="text-success flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                variant="default"
                fullWidth
                iconName="Download"
                iconPosition="left"
                onClick={() => handleDownload(version?.type)}
                className="mb-3"
              >
                Download {version?.type?.toUpperCase()}
              </Button>

              <div className="text-center">
                <button 
                  onClick={() => window.open(`/preview/resume.${version?.type}`, '_blank')}
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  Preview before download
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Information */}
        <div className="bg-muted/30 rounded-xl p-6 text-center">
          <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground mb-4">
            <div className="flex items-center space-x-2">
              <Icon name="Calendar" size={16} />
              <span>Last updated: {resumeData?.lastUpdated}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Globe" size={16} />
              <span>Available in: {resumeData?.languages?.join(', ')}</span>
            </div>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            Need a customized version for a specific role? 
          </p>
          
          <Button
            variant="outline"
            iconName="Mail"
            iconPosition="left"
            onClick={() => window.location.href = '/contact-form'}
          >
            Request Custom Resume
          </Button>
        </div>

        {/* Privacy Notice */}
        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground">
            <Icon name="Shield" size={14} className="inline mr-1" />
            Your download is secure and private. We don't track or store personal information.
          </p>
        </div>
      </div>
    </section>
  );
};

export default DownloadResume;